import React, { Component, } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import MapUtils from 'components/GoogleMaps/lib/MapUtils'

import MarkerWrapper from './MarkerWrapper'
import DefaultMarker from './DefaultMarker'

const mapEvents = ['center_changed']

class GoogleMap extends Component {
  static propTypes = {
    disableDefaultUI: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    style: PropTypes.any,
    initialZoom: PropTypes.number,
    minZoom: PropTypes.number,
    initialCenter: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    initialCenterUserLocation: PropTypes.bool,
    showLatLng: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    google: PropTypes.any.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    markers: PropTypes.array.isRequired,
    customMarker: PropTypes.func,
    markerSelected: PropTypes.func,
    panTo: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    setZoom: PropTypes.shape({
      timestamp: PropTypes.number,
      mode: PropTypes.oneOf(['zoomIn', 'zoomOut', 'value']),
      value: PropTypes.number,
    }),
  }

  static defaultProps = {
    disableDefaultUI: false,
    style: {
      width: '100%',
      height: '280px',
    },
    initialZoom: 8,
    minZoom: 2,
    initialCenter: {
      lat: 51.5062,
      lng: -0.116392,
    },
    initialCenterUserLocation: true,
    showLatLng: false,
    customMarker: DefaultMarker,
    markerSelected: () => {},
    panTo: {},
    setZoom: {},
  }

  state = {
    currentLocation: {
      lat: this.props.initialCenter.lat,
      lng: this.props.initialCenter.lng,
    },
    fetchingUserLocation: !!this.props.initialCenterUserLocation,
    markerRefs: [],
    lineRef: null,
  }

  componentDidMount() {
    if (this.props.initialCenterUserLocation) {
      if (navigator && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((pos) => {
          const coords = pos.coords
          this.setState(
            {
              fetchingUserLocation: false,
            },
            () => {
              this.recenterMap(coords.latitude, coords.longitude)
            }
          )
        })
      }
    }
    this.loadMap()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.google !== this.props.google ||
      prevProps.minZoom !== this.props.minZoom ||
      prevProps.initialZoom !== this.props.initialZoom
    ) {
      this.loadMap()
    } else if (prevProps.markers !== this.props.markers) {
      // check to see which markers have changed
      // note: as soon as one is found we need to change all later markers
      // as order is important
      let foundDiff = false
      const itemsToUpdate = []
      const itemCache = {}
      const prevMarkers = prevProps.markers
      this.props.markers.forEach((marker, index) => {
        itemCache[marker.id] = true
        if (foundDiff) {
          itemsToUpdate.push(marker.id)
        }
        if (!_.isEqual(marker, prevMarkers[index])) {
          foundDiff = true
          itemsToUpdate.push(marker.id)
        }
      })
      // update any removed markers
      prevMarkers.forEach((marker) => {
        if (!itemCache[marker.id]) {
          itemsToUpdate.push(marker.id)
        }
      })
      this.clearMapItems(itemsToUpdate)
      this.addMapItems(itemsToUpdate)
    }
    if (
      prevProps.panTo.lat !== this.props.panTo.lat ||
      prevProps.panTo.lng !== this.props.panTo.lng
    ) {
      // if panTo has changed (and exists) then pan to it
      const { google, panTo, } = this.props
      const { lat, lng, } = panTo
      if (lat && lng) {
        MapUtils.slowPanTo({
          google,
          map: this.map,
          lat,
          lng,
          intervals: 100,
          msec: 100,
        })
      }
    }
    if (prevProps.setZoom.timestamp !== this.props.setZoom.timestamp) {
      // setZoom has been newly issued so set the zoom level
      const { mode, value, } = this.props.setZoom
      const currentZoom = this.map.getZoom()
      switch (mode) {
        case 'zoomIn':
          this.map.setZoom(currentZoom + 1)
          break
        case 'zoomOut':
          this.map.setZoom(currentZoom - 1)
          break
        case 'value':
          this.map.setZoom(value)
          break
        default:
      }
    }
  }

  addMapItems = (itemsToAdd) => {
    const { markerRefs, } = this.state
    const { google, markers, customMarker, markerSelected, } = this.props
    const CustomMarker = MarkerWrapper({ google, })

    // add markers and line paths
    const linePaths = []
    // eslint-disable-next-line array-bracket-spacing
    const newMarkerRefs = [...markerRefs]
    if (markers) {
      markers.forEach((marker) => {
        if (!itemsToAdd || itemsToAdd.indexOf(marker.id) > -1) {
          const markerRef = new CustomMarker({
            lat: marker.lat,
            lng: marker.lng,
            map: this.map,
            marker,
            customMarker,
            markerSelected,
          })
          newMarkerRefs.push(markerRef)
        }
        linePaths.push({ lat: marker.lat, lng: marker.lng, })
      })
      const newLine = new google.maps.Polyline({
        path: linePaths,
        geodesic: true,
        strokeColor: '#e8691b',
        strokeOpacity: 1,
        strokeWeight: 2,
      })
      newLine.setMap(this.map)
      this.setState({
        markerRefs: newMarkerRefs,
        lineRef: newLine,
      })
    }
  }

  clearMapItems = (itemsToClear) => {
    // clear only updated markers
    const markersToKeep = []
    const { markerRefs, lineRef, } = this.state
    markerRefs.forEach((marker) => {
      if (!itemsToClear || itemsToClear.indexOf(marker.marker.id) > -1) {
        marker.setMap(null)
      } else {
        markersToKeep.push(marker)
      }
    })
    lineRef.setMap(null)
    this.setState({
      markerRefs: markersToKeep,
      lineRef: null,
    })
  }

  loadMap() {
    if (this.props && this.props.google) {
      const { google, initialZoom, minZoom, disableDefaultUI, } = this.props
      const { lat, lng, } = this.state.currentLocation
      const maps = google.maps

      const mapNode = this['google-map']
      const center = new maps.LatLng(lat, lng)
      const mapConfig = Object.assign(
        {},
        {
          center,
          zoom: initialZoom,
          minZoom,
          backgroundColor: 'none',
          disableDefaultUI,
        }
      )
      this.map = new maps.Map(mapNode, mapConfig)

      // add event listeners
      mapEvents.forEach((eventName) => {
        this.map.addListener(eventName, this.handleMapEvent(eventName))
      })

      // add map items
      this.addMapItems()
    }
  }

  recenterMap(lat, lng) {
    const map = this.map
    const google = this.props.google
    const maps = google.maps

    if (map) {
      const center = new maps.LatLng(lat, lng)
      this.setState(
        {
          currentLocation: {
            lat,
            lng,
          },
        },
        () => {
          map.panTo(center)
        }
      )
    }
  }

  handleMapEvent(eventName) {
    let timeout
    const thottleVal = 10
    return (event) => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      timeout = setTimeout(() => {
        this.mapEventHandler({
          eventName,
          event,
        })
      }, thottleVal)
    }
  }

  mapEventHandler({ eventName, }) {
    switch (eventName) {
      case 'center_changed':
        this.setState({
          currentLocation: {
            lat: this.map.center.lat(),
            lng: this.map.center.lng(),
          },
        })
        break
      default:
    }
  }

  render() {
    const { showLatLng, style: mapStyle, } = this.props
    const { currentLocation, fetchingUserLocation, } = this.state
    const style = {
      latlng: {
        textAlign: 'center',
        position: 'fixed',
        top: '0px',
        left: '0px',
        zIndex: 9999,
        right: '0px',
        backgroundColor: 'rgba(255, 255, 255, 0.86)',
        fontSize: '10px',
        color: 'rgba(147, 117, 84, 0.63)',
        padding: '2px',
      },
      latlngCrossHair: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        fontSize: '28px',
        fontWeight: '100',
        color: 'rgba(147, 117, 84, 0.63)',
      },
      map: mapStyle,
    }
    if (fetchingUserLocation) {
      // show loading icon or something?
      // style.visibility = 'hidden'
    }
    let latLng = ''
    if (showLatLng) {
      const latDisplay = currentLocation.lat.toFixed(5)
      const lngDisplay = currentLocation.lng.toFixed(5)
      latLng = (
        <div>
          <div style={style.latlng}>
            lat: {latDisplay}, lng: {lngDisplay}
          </div>
          <div style={style.latlngCrossHair}>+</div>
        </div>
      )
    }
    return (
      <div style={style.map}>
        {latLng}
        <div
          style={style.map}
          ref={(c) => {
            this['google-map'] = c
          }}
        />
      </div>
    )
  }
}

export default GoogleMap



// WEBPACK FOOTER //
// ./src/components/GoogleMaps/Map/GoogleMap.js